import { useEffect, useState } from "react"
import { addDays, differenceInCalendarDays } from "date-fns"
import { Link } from "react-router-dom"
import { AlertTriangle, CheckCircle2, Circle, Copy, FileDown, Loader2, Maximize2 } from "lucide-react"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { StatusBadge } from "@/components/ui/status-badge"
import { useToast } from "@/components/ui/use-toast"
import { ageFromDob, formatDateOnly, formatTimestamp, parseDateOnly, toDateOnlyString } from "@/lib/datetime"
import { cn } from "@/lib/utils"
import { tcmApi, tcmErrorMessage } from "../api"
import { useTcmAction, useTcmEpisode, useTcmNote } from "../hooks"
import { CLOSE_REASONS, closeReasonLabel, levelLabel, urgencyText, urgencyVariant } from "../lib/board"
import type { TcmActionRequest, TcmEpisodeDetail } from "../types"
import type { ClockAction } from "../lib/clock"
import { ClockRuler, TcmClock } from "./TcmClock"

interface TcmCaseDrawerProps {
  episodeId: string | null
  today: string
  ehrFor: (organizationId: string) => string
  onClose: () => void
  onCopy: (episodeId: string, ehr: string) => void
  copying: boolean
}

type FormMode = "contact" | "attempt" | "book" | "done" | "discharge" | "fix_discharge" | "close" | null

const md = (d: string | null | undefined) => formatDateOnly(d, "EEE M/d")

export function TcmCaseDrawer({ episodeId, today, ehrFor, onClose, onCopy, copying }: TcmCaseDrawerProps) {
  const { data: ep, isLoading } = useTcmEpisode(episodeId)
  const { data: note } = useTcmNote(episodeId, !!ep?.note)

  return (
    <Sheet open={!!episodeId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:w-[560px] sm:max-w-[560px] overflow-y-auto">
        {!ep || isLoading ? (
          <div className="flex items-center justify-center py-24">
            <SheetTitle className="sr-only">Loading case</SheetTitle>
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <DrawerBody
            ep={ep}
            today={today}
            ehr={ehrFor(ep.organization_id)}
            noteText={note?.text}
            onCopy={() => onCopy(ep.id, ehrFor(ep.organization_id))}
            copying={copying}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}

function DrawerBody({
  ep,
  today,
  ehr,
  noteText,
  onCopy,
  copying,
}: {
  ep: TcmEpisodeDetail
  today: string
  ehr: string
  noteText?: string
  onCopy: () => void
  copying: boolean
}) {
  const age = ageFromDob(ep.patient_dob)
  const isTcm = ep.kind === "TCM"
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <span>{ep.patient_name}</span>
          <Button asChild variant="outline" size="sm" className="ml-auto mr-6 h-7 gap-1.5 text-xs">
            <Link to={`/tcm/case/${ep.id}`}>
              <Maximize2 className="h-3.5 w-3.5" />
              Full case & care plan
            </Link>
          </Button>
        </SheetTitle>
        <SheetDescription asChild>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge variant={isTcm ? "success" : "info"}>{isTcm ? "TCM" : "ED follow-up"}</StatusBadge>
            <StatusBadge variant={urgencyVariant(ep.stage.urgency)} dot>{ep.stage.next_action}</StatusBadge>
            <span className="text-xs text-muted-foreground">
              {[ep.mrn && `MRN ${ep.mrn}`, ep.patient_dob && `DOB ${formatDateOnly(ep.patient_dob, "M/d/yyyy")}`, age !== null && `${age} y`]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
        </SheetDescription>
      </SheetHeader>

      <TcmCaseSections ep={ep} today={today} ehr={ehr} noteText={noteText} onCopy={onCopy} copying={copying} className="mt-6" />
    </>
  )
}

/** The case's working sections (next step and its forms, clock, requirements, stay, note, billing,
 *  activity): the drawer and the full case page show the same component, so they never drift. */
export function TcmCaseSections({
  ep,
  today,
  ehr,
  noteText,
  onCopy,
  copying,
  className,
}: {
  ep: TcmEpisodeDetail
  today: string
  ehr: string
  noteText?: string
  onCopy: () => void
  copying: boolean
  className?: string
}) {
  // A clock tooltip's action button opens the matching form in Next step.
  const [requested, setRequested] = useState<{ mode: ClockAction; at: number } | null>(null)
  return (
    <div className={cn("space-y-5", className)}>
      <Warnings ep={ep} />
      <NextStep ep={ep} today={today} requested={requested} />
      {ep.clock && (
        <ClockCard ep={ep} today={today} ehr={ehr} onAction={(mode) => setRequested({ mode, at: Date.now() })} />
      )}
      <Requirements ep={ep} />
      <StayCard ep={ep} />
      <NoteCard ep={ep} ehr={ehr} noteText={noteText} onCopy={onCopy} copying={copying} />
      {ep.kind === "TCM" && <BillingCard ep={ep} />}
      <Activity ep={ep} />
    </div>
  )
}

function Warnings({ ep }: { ep: TcmEpisodeDetail }) {
  const items: string[] = []
  if (ep.readmitted_on) {
    items.push(`Readmitted ${md(ep.readmitted_on)}, inside this case's 30 days. Only one TCM claim per patient per 30 days.`)
  }
  if (ep.within_prior_period) {
    items.push("This discharge falls inside an earlier case's 30 days. If that case was billed, this one can't be.")
  }
  if (ep.kind === "TCM" && ep.apcm_enrolled && !ep.closed_reason) {
    const visit = parseDateOnly(ep.f2f_completed_date ?? ep.f2f_scheduled_date)
    const month = visit ? visit.toLocaleDateString("en-US", { month: "long" }) : "the month of the visit"
    items.push(`Enrolled in APCM. Bill TCM, not APCM, for ${month}.`)
  }
  if (!items.length) return null
  return (
    <div className="space-y-2">
      {items.map((text) => (
        <div key={text} className="flex gap-2 rounded-xl border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{text}</span>
        </div>
      ))}
    </div>
  )
}

function NextStep({
  ep,
  today,
  requested,
}: {
  ep: TcmEpisodeDetail
  today: string
  requested: { mode: ClockAction; at: number } | null
}) {
  const { toast } = useToast()
  const action = useTcmAction(ep.id)
  const [mode, setMode] = useState<FormMode>(null)
  const [date, setDate] = useState(today)
  const [by, setBy] = useState("")
  const [text, setText] = useState("")
  const [reason, setReason] = useState("READMITTED")

  useEffect(() => setMode(null), [ep.id, ep.stage.code])
  useEffect(() => {
    if (!requested) return
    const mode = requested.mode
    open(mode, mode === "done" && ep.f2f_scheduled_date && ep.f2f_scheduled_date <= today ? ep.f2f_scheduled_date : today)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested])

  const open = (next: FormMode, defaultDate = today) => {
    setMode(next)
    setDate(defaultDate)
    setBy("")
    setText("")
  }

  const run = (body: TcmActionRequest, done: string) =>
    action.mutate(body, {
      onSuccess: () => {
        setMode(null)
        toast({ title: done })
      },
      onError: (err) => toast({ title: "Not saved", description: tcmErrorMessage(err), variant: "destructive" }),
    })

  const code = ep.stage.code
  const isTcm = ep.kind === "TCM"
  const start = ep.clock?.start ?? ep.post_acute_discharge_date ?? ep.discharge_date
  const suggestedVisit = (() => {
    const s = parseDateOnly(start)
    const t = parseDateOnly(today) as Date
    if (!s) return today
    const target = addDays(s, 6)
    return toDateOnlyString(target > t ? target : addDays(t, 1))
  })()
  const closed = code === "BILLED" || code === "CLOSED" || code === "FOLLOWED_UP"
  const busy = action.isPending

  const buttons: { label: string; primary?: boolean; onClick: () => void; disabled?: boolean }[] = []
  if (code === "CONTACT_DUE" || code === "CONTACT_OVERDUE") {
    buttons.push({ label: "Reached patient", primary: true, onClick: () => open("contact") })
    buttons.push({ label: `Log attempt (${ep.contact_attempts.length + 1} of 2)`, onClick: () => open("attempt") })
    if (!ep.f2f_scheduled_date) buttons.push({ label: "Book visit", onClick: () => open("book", suggestedVisit) })
  } else if (code === "BOOK_VISIT") {
    buttons.push({ label: "Book visit", primary: true, onClick: () => open("book", suggestedVisit) })
  } else if (code === "VISIT_UPCOMING" || code === "VISIT_OVERDUE") {
    buttons.push({ label: "Visit done", primary: true, onClick: () => open("done", ep.f2f_scheduled_date && ep.f2f_scheduled_date <= today ? ep.f2f_scheduled_date : today) })
    buttons.push({ label: "Reschedule", onClick: () => open("book", ep.f2f_scheduled_date ?? suggestedVisit) })
  } else if (code === "READY_TO_BILL" && isTcm) {
    // Visit done but no contact logged: log it first (it may have happened); billing needs it.
    const noContact = ep.contact_status === "PENDING"
    if (noContact) {
      buttons.push({ label: "Reached patient", primary: true, onClick: () => open("contact", ep.clock?.contact_due ?? today) })
      buttons.push({ label: `Log attempt (${ep.contact_attempts.length + 1} of 2)`, onClick: () => open("attempt", ep.clock?.contact_due ?? today) })
    }
    for (const c of ["99496", "99495"]) {
      const allowed = ep.clock?.allowed_codes.includes(c) ?? false
      buttons.push({
        label: `Billed ${c}`,
        primary: allowed && !noContact && c === ep.clock?.allowed_codes[0],
        disabled: !allowed || noContact,
        onClick: () => run({ action: "mark_billed", code: c }, `Marked billed ${c}`),
      })
    }
  } else if (code === "RECORDS_NEEDED" && !ep.discharge_date) {
    buttons.push({ label: "Record discharge", onClick: () => open("discharge") })
  } else if (code === "IN_HOSPITAL") {
    buttons.push({ label: "Record discharge", primary: true, onClick: () => open("discharge") })
  }

  const dayHint = (() => {
    const s = parseDateOnly(start)
    const d = parseDateOnly(date)
    if (!isTcm || !s || !d || mode !== "book") return null
    const day = differenceInCalendarDays(d, s)
    if (day <= 7) return { text: `Day ${day}: 99496 or 99495`, cls: "text-success" }
    if (day <= 14) return { text: `Day ${day}: 99495 only`, cls: "text-warning" }
    return { text: `Day ${day}: past the TCM window`, cls: "text-destructive" }
  })()

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Next step</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className={cn("text-sm font-medium", urgencyText(ep.stage.urgency))}>{ep.stage.next_action}</p>
          {(ep.stage.due_date || ep.stage.detail) && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {ep.stage.due_date && ep.stage.urgency !== "done" && `Due ${md(ep.stage.due_date)}. `}
              {ep.stage.detail}
            </p>
          )}
        </div>

        {buttons.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {buttons.map((b) => (
              <Button
                key={b.label}
                size="sm"
                variant={b.primary ? "default" : "outline"}
                disabled={busy || b.disabled}
                onClick={b.onClick}
              >
                {b.label}
              </Button>
            ))}
          </div>
        )}

        {mode && mode !== "close" && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="tcm-date" className="text-xs">
                  {mode === "book" ? "Visit date" : mode === "discharge" ? "Discharge date" : mode === "fix_discharge" ? "Correct discharge date" : mode === "done" ? "Visit date" : "Date"}
                </Label>
                <Input id="tcm-date" type="date" value={date} max={mode === "book" ? undefined : today} onChange={(e) => setDate(e.target.value)} />
              </div>
              {(mode === "contact" || mode === "attempt") && (
                <div className="space-y-1.5">
                  <Label htmlFor="tcm-by" className="text-xs">By (optional)</Label>
                  <Input id="tcm-by" value={by} onChange={(e) => setBy(e.target.value)} placeholder="Who made the call" />
                </div>
              )}
              {mode === "book" && (
                <div className="space-y-1.5">
                  <Label htmlFor="tcm-provider" className="text-xs">Provider (optional)</Label>
                  <Input id="tcm-provider" value={by} onChange={(e) => setBy(e.target.value)} placeholder={ep.f2f_provider ?? ""} />
                </div>
              )}
            </div>
            {mode === "attempt" && (
              <div className="space-y-1.5">
                <Label htmlFor="tcm-note" className="text-xs">Note (optional)</Label>
                <Input id="tcm-note" value={text} onChange={(e) => setText(e.target.value)} placeholder="Left voicemail" />
              </div>
            )}
            {dayHint && <p className={cn("text-xs font-medium", dayHint.cls)}>{dayHint.text}</p>}
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={busy || !date}
                onClick={() => {
                  if (mode === "contact") run({ action: "log_contact", date, by: by || undefined }, "Contact logged")
                  if (mode === "attempt") run({ action: "log_attempt", date, by: by || undefined, note: text || undefined }, "Attempt logged")
                  if (mode === "book") run({ action: "book_visit", date, provider: by || undefined }, `Visit booked for ${md(date)}`)
                  if (mode === "done") run({ action: "visit_done", date }, "Visit marked done")
                  if (mode === "discharge") run({ action: "record_discharge", date }, "Discharge recorded. The 30-day clock has started.")
                  if (mode === "fix_discharge") run({ action: "record_discharge", date }, `Discharge date corrected to ${md(date)}`)
                }}
              >
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMode(null)}>Cancel</Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          {closed ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => run({ action: "reopen" }, "Case reopened")}>
              Reopen
            </Button>
          ) : mode === "close" ? (
            <>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="h-8 w-[180px] text-xs" aria-label="Reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLOSE_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" disabled={busy}
                onClick={() => run({ action: "close", reason }, "Case closed")}>
                Close case
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMode(null)}>Cancel</Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setMode("close")}>
                Close case…
              </Button>
              {ep.discharge_date && !ep.f2f_completed_date && (
                <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => open("fix_discharge", ep.discharge_date as string)}>
                  Correct discharge date…
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

/** The contact was recorded but misses the deadline: reached late, or fewer than two attempts by it. */
function isLateContact(ep: TcmEpisodeDetail): boolean {
  const due = ep.clock?.contact_due
  if (!due) return false
  if (ep.contact_status === "DONE") return !!ep.contact_date && ep.contact_date > due
  if (ep.contact_status === "ATTEMPTED_2X") return ep.contact_attempts.filter((a) => a.date && a.date <= due).length < 2
  return false
}

function ClockCard({
  ep,
  today,
  ehr,
  onAction,
}: {
  ep: TcmEpisodeDetail
  today: string
  ehr: string
  onAction: (mode: ClockAction) => void
}) {
  const c = ep.clock!
  const late = isLateContact(ep)
  const contactMet = !late && (ep.contact_status === "DONE" || ep.contact_status === "ATTEMPTED_2X")
  const milestones = [
    { label: "2-day contact", date: c.contact_due, tone: contactMet ? "text-success" : late || ep.stage.code === "CONTACT_OVERDUE" ? "text-destructive" : "" },
    { label: "99496 by (day 7)", date: c.high_complexity_by, tone: c.day > 7 ? "text-muted-foreground" : "" },
    { label: "99495 by (day 14)", date: c.moderate_complexity_by, tone: c.day > 14 ? "text-muted-foreground" : "" },
    { label: "Period ends (day 29)", date: c.period_end, tone: "" },
  ]
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span>30-day clock</span>
          <span className="text-xs font-normal text-muted-foreground">
            Day {c.day} · {md(c.start)} to {md(c.period_end)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1 pt-1">
          <ClockRuler />
          <TcmClock episode={ep} today={today} ehr={ehr} size="lg" onAction={onAction} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {milestones.map((m) => (
            <div key={m.label} className="rounded-lg border bg-muted/30 px-3 py-2">
              <div className="text-[11px] text-muted-foreground">{m.label}</div>
              <div className={cn("text-sm font-medium", m.tone)}>{md(m.date)}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function Requirements({ ep }: { ep: TcmEpisodeDetail }) {
  const isTcm = ep.kind === "TCM"
  const rows: { title: string; done: boolean; detail: string; late?: boolean }[] = []
  if (isTcm) {
    const late = isLateContact(ep)
    rows.push({
      title: "Interactive contact within 2 business days",
      done: !late && (ep.contact_status === "DONE" || ep.contact_status === "ATTEMPTED_2X"),
      late: late || ep.stage.code === "CONTACT_OVERDUE",
      detail:
        ep.contact_status === "DONE"
          ? `Reached ${md(ep.contact_date)}${ep.contact_by ? ` by ${ep.contact_by}` : ""}` +
            (late ? `, after the ${md(ep.clock?.contact_due)} deadline: not TCM-billable` : "")
          : ep.contact_status === "ATTEMPTED_2X"
            ? late
              ? `Fewer than two attempts by the ${md(ep.clock?.contact_due)} deadline: not TCM-billable`
              : `${ep.contact_attempts.length} documented attempts; requirement met`
            : ep.clock
              ? `Due ${md(ep.clock.contact_due)}${ep.contact_attempts.length ? ` · ${ep.contact_attempts.length} attempt logged` : ""}`
              : "Starts at discharge",
    })
  }
  rows.push({
    title: "Face-to-face visit",
    done: !!ep.f2f_completed_date,
    late: ep.stage.code === "VISIT_OVERDUE" || (ep.stage.code === "BOOK_VISIT" && ep.stage.urgency === "red"),
    detail: ep.f2f_completed_date
      ? `Seen ${md(ep.f2f_completed_date)}${ep.clock?.visit_day != null ? ` (day ${ep.clock.visit_day})` : ""}`
      : ep.f2f_scheduled_date
        ? `Booked ${md(ep.f2f_scheduled_date)}${ep.clock?.visit_day != null ? ` (day ${ep.clock.visit_day})` : ""}${ep.f2f_provider ? ` with ${ep.f2f_provider}` : ""}`
        : "Not booked",
  })
  rows.push({
    title: "Medication reconciliation",
    done: !!ep.f2f_completed_date,
    detail: "No later than the face-to-face visit. The note lists the discharge changes.",
  })
  if (isTcm) {
    rows.push({
      title: "Claim",
      done: !!ep.billed_code,
      detail: ep.billed_code
        ? `Billed ${ep.billed_code} on ${md(ep.billed_date)}`
        : ep.f2f_completed_date
          ? "Ready now. The claim can carry the visit date."
          : "After the visit",
    })
  }
  return (
    <div>
      <h3 className="text-sm font-medium mb-2">TCM requirements</h3>
      <ul className="divide-y rounded-xl border">
        {rows.map((r) => (
          <li key={r.title} className="flex gap-3 px-3 py-2.5">
            {r.done ? (
              <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" aria-label="Done" />
            ) : (
              <Circle className={cn("h-4 w-4 shrink-0 mt-0.5", r.late ? "text-destructive" : "text-muted-foreground/60")} aria-label="Not done" />
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium">{r.title}</div>
              <div className="text-xs text-muted-foreground">{r.detail}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function StayCard({ ep }: { ep: TcmEpisodeDetail }) {
  const rows: [string, string][] = [
    ["Facility", ep.facility ?? "—"],
    ["Level of care", levelLabel(ep.level_of_care)],
    ["Admitted", md(ep.admit_date) || "—"],
    ["Discharged", ep.discharge_date ? md(ep.discharge_date) : "Still admitted"],
  ]
  if (ep.post_acute_facility) {
    rows.push(["Then", ep.post_acute_facility])
    rows.push(["Home from SNF", md(ep.post_acute_discharge_date) || "Still there"])
  }
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Hospital stay</CardTitle>
      </CardHeader>
      <CardContent className="text-sm space-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-medium text-right">{v}</span>
          </div>
        ))}
        {ep.post_acute_facility && (
          <p className="pt-2 text-xs text-info">The clock starts at the SNF discharge, not the hospital discharge.</p>
        )}
      </CardContent>
    </Card>
  )
}

function NoteCard({
  ep,
  ehr,
  noteText,
  onCopy,
  copying,
}: {
  ep: TcmEpisodeDetail
  ehr: string
  noteText?: string
  onCopy: () => void
  copying: boolean
}) {
  const { toast } = useToast()
  const latest = ep.note
  const status =
    ep.ehr_status === "filed"
      ? { text: `Filed in ${ehr} by Pear${latest?.ehr_filed_at ? ` ${formatTimestamp(latest.ehr_filed_at, "M/d h:mm a")}` : ""}`, cls: "text-success" }
      : ep.ehr_status === "stale"
        ? { text: `An older version is in ${ehr}. Copy this one to replace it.`, cls: "text-warning" }
        : { text: `Not in your ${ehr} yet. Copy it from here.`, cls: "text-warning" }

  const openPdf = async () => {
    try {
      const blob = await tcmApi.getNotePdf(ep.id)
      window.open(URL.createObjectURL(blob), "_blank")
    } catch (err) {
      toast({ title: "Could not open the PDF", description: tcmErrorMessage(err), variant: "destructive" })
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span>{ep.kind === "TCM" ? "TCM note" : "ED follow-up note"}</span>
          {latest && (
            <div className="flex gap-2">
              {latest.has_pdf && (
                <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={openPdf}>
                  <FileDown className="h-3 w-3" />
                  PDF
                </Button>
              )}
              <Button size="sm" className="h-7 gap-1.5 text-xs" onClick={onCopy} disabled={copying}>
                {copying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Copy className="h-3 w-3" />}
                Copy for {ehr}
              </Button>
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {latest ? (
          <>
            <p className={cn("text-xs font-medium", status.cls)}>{status.text}</p>
            <div className="max-h-72 overflow-y-auto rounded-lg border bg-muted/20 p-3 text-[13px] leading-relaxed whitespace-pre-wrap">
              {noteText ?? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Version {latest.version} · uploaded {formatTimestamp(latest.created_at, "MMM d, h:mm a")}
              {ep.notes.length > 1 && ` · ${ep.notes.length} versions`}
            </p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            {ep.discharge_date
              ? `Pear posts the note here once the discharge records from ${ep.facility ?? "the hospital"} are in.`
              : "Pear writes the note after discharge, once the records arrive."}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function BillingCard({ ep }: { ep: TcmEpisodeDetail }) {
  const c = ep.clock
  const late = isLateContact(ep)
  const allowed = late || !c ? [] : c.visit_day != null ? c.allowed_codes : c.day <= 7 ? ["99496", "99495"] : c.day <= 14 ? ["99495"] : []
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Billing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex flex-wrap gap-2">
          {["99496", "99495"].map((code) => (
            <StatusBadge key={code} variant={allowed.includes(code) ? "success" : "muted"}>
              {code} {allowed.includes(code) ? "allowed" : "not allowed"}
            </StatusBadge>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {ep.billed_code
            ? `Billed ${ep.billed_code}.`
            : late
              ? "The 2-day contact was late, so TCM can't be billed. Bill the visit as a regular E/M."
              : c?.visit_day != null
              ? `Visit on day ${c.visit_day}. The provider picks the code from the complexity of decision-making (99496 needs high complexity). One TCM claim per patient per 30 days.`
              : "The code depends on the visit day and complexity. One TCM claim per patient per 30 days."}
        </p>
        {ep.closed_reason && <p className="text-xs text-muted-foreground">Closed: {closeReasonLabel(ep.closed_reason)}</p>}
      </CardContent>
    </Card>
  )
}

const STEP_LABELS: Record<string, string> = {
  "contact:done": "reached the patient",
  "contact:attempt": "logged a contact attempt",
  "visit:booked": "booked the visit",
  "visit:done": "completed the visit",
  "billing:billed": "billed",
  "case:closed": "closed the case",
  "case:reopened": "reopened the case",
  "stay:discharged": "recorded the discharge",
  "stay:discharge_corrected": "corrected the discharge date",
  "note:posted": "posted the note",
  "ehr:filed": "filed the note in the EHR",
}

function Activity({ ep }: { ep: TcmEpisodeDetail }) {
  if (!ep.events.length) return null
  return (
    <div>
      <h3 className="text-sm font-medium mb-2">Activity</h3>
      <ul className="space-y-2">
        {ep.events.map((e, i) => (
          <li key={i} className="flex gap-3 text-xs">
            <span className="w-24 shrink-0 text-muted-foreground">{formatTimestamp(e.at, "MMM d, h:mm a")}</span>
            <span>
              <span className="font-medium">{e.actor === "pear-pipeline" ? "Pear" : e.actor}</span>{" "}
              {STEP_LABELS[`${e.step}:${e.state}`] ?? `${e.step} ${e.state}`}
              {e.detail && <span className="text-muted-foreground"> · {e.detail}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
